import React from 'react';
import classNames from 'classnames';
import _ from 'lodash';
import { useTranslation } from 'react-i18next';
import { Checkbox as QomponentCheckbox, Icon } from '@seeqdev/qomponents';
import { TooltipPosition } from '@seeqdev/qomponents/dist/Tooltip/Tooltip.types';
import { KEY_CODES } from '@/main/app.constants';
import { HoverTooltip } from '@/core/HoverTooltip.atom';

interface CheckboxProps {
  id: string;
  isChecked: boolean;
  label: string | React.ReactNode;
  extraLabel?: React.ReactElement;
  onChange?: (event: any) => void;
  onClick?: (event: any) => void;
  value?: any;
  type?: 'radio' | 'checkbox';
  disabled?: boolean;
  classes?: string;
  tooltip?: string;
  tooltipPlacement?: TooltipPosition;
  isTooltipSimple?: boolean;
  description?: string;
  skipMemo?: boolean;
  descriptionClasses?: string;
  onHelpIconClick?: () => any;
}

export const CheckboxUnwrapped: React.FunctionComponent<CheckboxProps> = (props) => {
  const {
    onChange = _.noop,
    isChecked,
    label,
    extraLabel,
    id,
    disabled,
    type = 'checkbox',
    classes,
    tooltip,
    tooltipPlacement = 'bottom',
    isTooltipSimple = false,
    description,
    value,
    descriptionClasses = 'ml20 pl2 mtn5 width-200 font-size-smaller',
    onHelpIconClick,
    onClick,
  } = props;

  const { t } = useTranslation();

  const withTooltip = (checkboxComponent: React.JSX.Element) => (
    <HoverTooltip text={tooltip} placement="top">
      {checkboxComponent}
    </HoverTooltip>
  );

  const checkboxComponent = (
    <span
      className={classNames(
        'flexColumnContainer',
        'flexAlignStart',
        classes,
        isChecked ? 'specChecked' : 'specNotChecked',
      )}
      data-testid={id}>
      <div className={classNames('flexColumnContainer sq-checkbox')}>
        <QomponentCheckbox
          value={value}
          onKeyDown={(e) => e.keyCode === KEY_CODES.ENTER && e.currentTarget.click()}
          extraClassNames="mb3"
          onChange={onChange}
          onClick={onClick}
          checked={isChecked}
          type={type}
          id={id}
          testId={`${id}-input`}
          disabled={disabled}
          label={typeof label === 'string' ? t(label) : label}
        />
        {extraLabel}
      </div>
      {!isTooltipSimple && tooltip && (
        <Icon
          icon="fa-question-circle"
          large={true}
          tooltip={t(tooltip)}
          tooltipPlacement={tooltipPlacement}
          extraClassNames="pl5"
          onClick={onHelpIconClick}
          testId={`${id}Tooltip`}
        />
      )}
      {description && (
        <div className={classNames('text-italic sq-fairly-dark-gray', descriptionClasses)}>{t(description)}</div>
      )}
    </span>
  );
  return isTooltipSimple ? withTooltip(checkboxComponent) : checkboxComponent;
};

/**
 * Basic checkbox
 */
export const Checkbox = React.memo(
  CheckboxUnwrapped,
  (prev, next) =>
    !(
      prev.skipMemo ||
      prev.isChecked !== next.isChecked ||
      prev.label !== next.label ||
      prev.extraLabel !== next.extraLabel ||
      !_.isEqual(prev.value, next.value) ||
      prev.disabled !== next.disabled ||
      prev.classes !== next.classes
    ),
);
